"use client";

import { DEFAULT_LOCAL_UI_SETTINGS, LOCAL_UI_SETTINGS_KEY, type LocalUiSettingsPath, type LocalUiSettingsPathValue, type LocalUiSettingsV1, parseLocalUiSettingsV1 } from "./local-ui-settings.schema";
import { getAtPath, setAtPathImmutable } from "@/lib/local-ui/immutable-path";
import { useCallback, useSyncExternalStore } from "react";

type Listener = () => void;
type SetterInput<P extends LocalUiSettingsPath> = LocalUiSettingsPathValue<P> | ((prev: LocalUiSettingsPathValue<P>) => LocalUiSettingsPathValue<P>);

export type SetLocalUiSetting<P extends LocalUiSettingsPath> = (next: SetterInput<P>) => void;

const listenersByPath = new Map<LocalUiSettingsPath, Set<Listener>>();
let state: LocalUiSettingsV1 = DEFAULT_LOCAL_UI_SETTINGS;
let browserSyncStarted = false;

function isUpdater<P extends LocalUiSettingsPath>(value: SetterInput<P>): value is (prev: LocalUiSettingsPathValue<P>) => LocalUiSettingsPathValue<P> {
    return typeof value === "function";
}

function toRecord(value: unknown): Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

function readFromStorage(): LocalUiSettingsV1 {
    if (typeof window === "undefined") {
        return DEFAULT_LOCAL_UI_SETTINGS;
    }

    try {
        const raw = window.localStorage.getItem(LOCAL_UI_SETTINGS_KEY);
        if (raw === null) {
            return DEFAULT_LOCAL_UI_SETTINGS;
        }
        return parseLocalUiSettingsV1(JSON.parse(raw)) ?? DEFAULT_LOCAL_UI_SETTINGS;
    } catch {
        return DEFAULT_LOCAL_UI_SETTINGS;
    }
}

function writeToStorage(next: LocalUiSettingsV1): boolean {
    if (typeof window === "undefined") {
        return false;
    }

    try {
        window.localStorage.setItem(LOCAL_UI_SETTINGS_KEY, JSON.stringify(next));
        return true;
    } catch {
        return false;
    }
}

function getDefaultPathValue<P extends LocalUiSettingsPath>(path: P): LocalUiSettingsPathValue<P> {
    return getAtPath(DEFAULT_LOCAL_UI_SETTINGS, path) as LocalUiSettingsPathValue<P>;
}

function getPathValue<P extends LocalUiSettingsPath>(path: P): LocalUiSettingsPathValue<P> {
    const value = getAtPath(state, path);
    return value === undefined ? getDefaultPathValue(path) : (value as LocalUiSettingsPathValue<P>);
}

function notifyChangedPaths(previous: LocalUiSettingsV1, next: LocalUiSettingsV1): void {
    for (const [path, listeners] of listenersByPath) {
        const previousValue = getAtPath(previous, path);
        const nextValue = getAtPath(next, path);
        if (Object.is(previousValue, nextValue)) {
            continue;
        }

        for (const listener of Array.from(listeners)) {
            listener();
        }
    }
}

function replaceState(next: LocalUiSettingsV1): void {
    const previous = state;
    state = next;
    notifyChangedPaths(previous, next);
}

function onStorage(event: StorageEvent): void {
    if (event.key !== null && event.key !== LOCAL_UI_SETTINGS_KEY) {
        return;
    }
    replaceState(readFromStorage());
}

function startBrowserSync(): void {
    if (browserSyncStarted || typeof window === "undefined") {
        return;
    }

    browserSyncStarted = true;
    window.addEventListener("storage", onStorage);
    replaceState(readFromStorage());
}

function subscribePath(path: LocalUiSettingsPath, listener: Listener): () => void {
    let listeners = listenersByPath.get(path);
    if (!listeners) {
        listeners = new Set();
        listenersByPath.set(path, listeners);
    }

    listeners.add(listener);
    startBrowserSync();

    return () => {
        listeners?.delete(listener);
        if (listeners?.size === 0) {
            listenersByPath.delete(path);
        }
    };
}

function setPathValue<P extends LocalUiSettingsPath>(path: P, next: SetterInput<P>): void {
    const current = getPathValue(path);
    const resolved = isUpdater(next) ? next(current) : next;
    if (Object.is(current, resolved)) {
        return;
    }

    const patched = setAtPathImmutable(state, path, resolved);
    const candidate = { ...toRecord(patched), version: 1, updatedAt: Date.now() };
    const parsed = parseLocalUiSettingsV1(candidate);
    if (!parsed) {
        return;
    }

    const previous = state;
    state = parsed;
    writeToStorage(parsed);
    notifyChangedPaths(previous, parsed);
}

export { setPathValue as setLocalUiSetting };

/** The stored value right now, for a component that must know it on its
 *  first render, before it has subscribed (the dashboard grid seeds which
 *  widgets are closed from it, so its first paint is the closed layout). On
 *  the server this is the default, as the server snapshot is. */
export function getLocalUiSetting<P extends LocalUiSettingsPath>(path: P): LocalUiSettingsPathValue<P> {
    startBrowserSync();
    return getPathValue(path);
}

export function useLocalUiSetting<P extends LocalUiSettingsPath>(path: P): readonly [LocalUiSettingsPathValue<P>, SetLocalUiSetting<P>] {
    const subscribe = useCallback((listener: Listener) => subscribePath(path, listener), [path]);
    const getSnapshot = useCallback(() => getPathValue(path), [path]);
    const getServerSnapshot = useCallback(() => getDefaultPathValue(path), [path]);
    const value = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

    const setValue = useCallback<SetLocalUiSetting<P>>((next) => {
        setPathValue(path, next);
    }, [path]);

    return [value, setValue] as const;
}
